import { FunctionComponent, memo, useCallback, useContext, useEffect, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { ChannelContext } from '../../context';
import { StorySettings, StoryStatus } from '../../services';
import {
  getAppTabActionByStoryTabType,
  getLocale,
  StoryTabType,
  useAppDispatch,
  useAppSelector,
} from '../../shared';
import { getStories } from '../../slices';
import { NoContent } from '../Error';
import { StoryList } from './StoryList';

import classes from './StoryTabList.module.scss';

interface StoryTabListProps {
  storyTabType: StoryTabType;
}

export const StoryTabList: FunctionComponent<StoryTabListProps> = memo(({ storyTabType }) => {
  const dispatch = useAppDispatch();

  const { id: channelId, appTabs } = useContext(ChannelContext).channel;

  const { stories, isFetched: isFetchedStories } = useAppSelector(({ stories }) => stories);

  useEffect(() => {
    dispatch(
      getStories({ channelId, filter: { onlyPostIn: false, status: [StoryStatus.PUBLISHED] } })
    );
  }, [channelId, dispatch]);

  const includeCondition = useCallback(
    ({
      includeInMixTab,
      includeInMixTab2,
      includeInMixTab3,
      includeInMixTab4,
      includeInMixTab5,
    }: StorySettings) => {
      switch (storyTabType) {
        case StoryTabType.storyTab1:
          return includeInMixTab;
        case StoryTabType.storyTab2:
          return includeInMixTab2;
        case StoryTabType.storyTab3:
          return includeInMixTab3;
        case StoryTabType.storyTab4:
          return includeInMixTab4;
        case StoryTabType.storyTab5:
          return includeInMixTab5;
        default:
          return true;
      }
    },
    [storyTabType]
  );

  const filteredStories = useMemo(() => {
    return stories?.filter(({ settings }) => includeCondition(settings)) ?? [];
  }, [includeCondition, stories]);

  const content = useMemo(() => {
    if (!isFetchedStories) {
      return (
        <div className={classes['story-tab-list__loader']}>
          <Skeleton height={'5rem'} />
          <Skeleton height={'5rem'} />
          <Skeleton height={'5rem'} />
        </div>
      );
    }

    if (!filteredStories.length) {
      return <NoContent />;
    }

    return <StoryList stories={filteredStories} storyTabType={storyTabType} />;
  }, [filteredStories, isFetchedStories, storyTabType]);

  const appTabLabel = useMemo(() => {
    const appTabTitle = appTabs.find(
      ({ action }) => action.type === getAppTabActionByStoryTabType(storyTabType)
    )?.title;

    return appTabTitle?.find(({ language }) => language === getLocale())?.localization;
  }, [appTabs, storyTabType]);

  return (
    <div className={classes['story-tab-list']}>
      <h1 className={classes['story-tab-list__title']}>{appTabLabel}</h1>
      {content}
    </div>
  );
});
